import { TRawProperty, TRawPropertyMember } from "@propely/propely-typings";
import { fetchFeatureFlags } from "@src/contexts/FeatureFlagContext";
import { updateLocalDataActionValidator } from "@src/data-hooks/actionValidator/actionValidator.localData";
import { fetchAPIActionValidator } from "@src/data-hooks/actionValidator/actionValidator.service";
import { usePropertyMemberKey } from "@src/data-hooks/property/member/propertyMembers.service";
import { usePropertyKey } from "@src/data-hooks/property/property.service";
import { queryClient } from "@src/index";
import { resetPropelyApi } from "@src/libs/firebase/api";
import { setSentryUser } from "@src/libs/sentry/sentry";
import { getSingularRoleTitle } from "@src/utils/data/roles.utils";
import { AppData } from "AppData";
import { User, getAuth, onAuthStateChanged } from "firebase/auth";
import { Tag } from "../../../data-hooks/company/tags/tags.type";
import { ApiClientData } from "../../../libs/firebase/ApiClientData";
import { Debug } from "../../../utils/debug";
import { ActivityData } from "../Activity/ActivityData";
import { ChatData } from "../Chat/ChatData";
import { CollectionData } from "../collections/CollectionData";
import { CollectionFieldData } from "../collections/CollectionFieldData";
import { CompanyCollectionData } from "../Company/Collections/CompanyCollectionData";
import { CompanyData } from "../Company/CompanyData";
import { GlobalDataTypeData } from "../Company/GlobalDataTypeData";
import { ContractorGroupData } from "../ContractorGroup/ContractorGroupData";
import { CustomReportData } from "../CustomReport/CustomReportData";
import { EventData } from "../Event/EventData";
import { GlobalFileData } from "../Files/GlobalFileData";
import { InboxEmailData } from "../InboxEmail/InboxEmailData";
import { PostData } from "../Post/PostData";
import { AppModuleID } from "../Property/AppModules";
import { PropertyData } from "../Property/PropertyData";
import { UnitData } from "../Property/Units/UnitData";
import { RoutineData } from "../Routines/RoutineData";
import { RoutinesLoader } from "../Routines/RoutinesLoader";
import { SurveyData } from "../Survey/SurveyData";
import { SurveysLoader } from "../Survey/SurveysLoader";
import { TrashcanItemData } from "../Trashcan/TrashcanItemData";
import { Role } from "../User/Role";
import { UserData } from "../User/UserData";
import CollectionLoader from "./CollectionLoader";
import { DataObjectHelpers } from "./DataObjects/DataObjectHelpers";
import { FirebaseViewData } from "./FirebaseViewData";
import { V2CollectionItemData } from "./V2CollectionItemData";

/**
 * @deprecated Use React-Query based data fetching instead.
 * More information on Notion or ask the team on Slack.
 */
export class LocalData {
  static idToken: string = "";

  /**
   * @deprecated Use useCurrentUser hook instead.
   */
  static currentUser: UserData;
  static units: UnitData[] = [];
  static surveysLoader = new SurveysLoader();
  static routinesLoader = new RoutinesLoader();

  static activities: CollectionLoader<ActivityData> = new CollectionLoader("activities", undefined, ActivityData);
  static apiClients: CollectionLoader<ApiClientData> = new CollectionLoader("apiClients", undefined, ApiClientData);
  static chats: CollectionLoader<ChatData> = new CollectionLoader("chats", undefined, ChatData);
  static collections: CollectionLoader<CollectionData> = new CollectionLoader("collections", undefined, CollectionData);
  static collectionFields: CollectionLoader<CollectionFieldData> = new CollectionLoader("collectionFields", undefined, CollectionFieldData);
  static collectionItems: CollectionLoader<V2CollectionItemData> = new CollectionLoader("collectionItems", undefined, V2CollectionItemData);
  static collectionTemplates: CollectionLoader<CompanyCollectionData> = new CollectionLoader("collectionTemplates", undefined, CompanyCollectionData);
  static companies: CollectionLoader<CompanyData> = new CollectionLoader("companies", undefined, CompanyData);
  static contractorGroups: CollectionLoader<ContractorGroupData> = new CollectionLoader("contractorGroups", undefined, ContractorGroupData);
  static customReports: CollectionLoader<CustomReportData> = new CollectionLoader("customReports", undefined, CustomReportData);
  static customReportTemplates: CollectionLoader<CustomReportData> = new CollectionLoader("customReportTemplates", undefined, CustomReportData);
  static dataTypes: CollectionLoader<GlobalDataTypeData> = new CollectionLoader("dataTypes", undefined, GlobalDataTypeData);
  static events: CollectionLoader<EventData> = new CollectionLoader("events", undefined, EventData);
  static files: CollectionLoader<GlobalFileData> = new CollectionLoader("files", undefined, GlobalFileData);
  static inboxEmails: CollectionLoader<InboxEmailData> = new CollectionLoader("inboxEmails", undefined, InboxEmailData);
  static posts: CollectionLoader<PostData> = new CollectionLoader("posts", undefined, PostData);
  static properties: CollectionLoader<PropertyData> = new CollectionLoader("properties", undefined, PropertyData);
  static routines: CollectionLoader<RoutineData> = new CollectionLoader("routines", undefined, RoutineData);
  static surveys: CollectionLoader<SurveyData> = new CollectionLoader("reports", undefined, SurveyData);
  static trashcan: CollectionLoader<TrashcanItemData> = new CollectionLoader("trashcan", undefined, TrashcanItemData);
  static users: CollectionLoader<UserData> = new CollectionLoader("users", undefined, UserData);
  static views: CollectionLoader<FirebaseViewData> = new CollectionLoader("views", undefined, FirebaseViewData);

  /**
   * @deprecated Use useTags hook instead.
   * Note. This is a temporary solution to store tags from new data fetching approach to some old places which requires static types in the old data fetching approach (like data object helpers)
   */
  static staticTags: Tag[] = [];

  static getTagWithId(id: string): Tag | undefined {
    return this.staticTags.find((tag) => tag.id === id);
  }

  static getUnitWithId(id: string): UnitData {
    for (const property of this.properties.documents) {
      for (const unit of property.units) {
        if (unit.id === id) return unit;
      }
    }
    for (let i in this.units) {
      if (this.units[i].id === id) {
        return this.units[i];
      }
    }
    let unit = new UnitData(id, "units", undefined);
    this.units.push(unit);
    return unit;
  }

  /**
   * @deprecated Use useModulesStatus-hook instead
   */
  static moduleActivatedOnAnyProperties(module: AppModuleID): boolean {
    for (let property of LocalData.currentUser.properties.filter((p) => p.name && !p.deleted)) {
      if (property.moduleIsActivated(module)) {
        return true;
      }
    }
    return false;
  }

  static async moduleActivatedOnCompany(company: CompanyData, module: AppModuleID): Promise<boolean> {
    const modules = await company.modules.loadWithPromise();
    return modules.map((m) => m.id).includes(module);
  }

  static initializeUser(completion: (success: boolean) => void) {
    //console.log("InitializeUser");
    let user: UserData | undefined = undefined;
    if (LocalData.currentUser && LocalData.currentUser.isExternal) {
      user = LocalData.currentUser;
      continueLoading();
    } else {
      getUser((loadedUser) => {
        user = loadedUser;
        continueLoading();
      });
    }

    function continueLoading() {
      if (user && user.email) {
        Debug.print("Loading intial user data");

        // Updates logged in devices
        user.saveCurrentDevice();
        AppData.getRemoteConfig();

        const dataLoaded = () => {
          loadedCount++;
          if (loadedCount >= totalCount) {
            LocalData.currentUser.properties = LocalData.currentUser.properties.filter((property) => !property.deleted);
            DataObjectHelpers.propertyMembers.units.title = getSingularRoleTitle(Role.RENTER.id, undefined);

            setSentryUser(user ?? null);

            completion(true);
            return;
          }
        };

        LocalData.currentUser.notifications?.setupRealtimeListener(() => {});

        let totalCount: number = 2;
        let loadedCount: number = 0;

        fetchAPIActionValidator()
          .then((data) => {
            updateLocalDataActionValidator(data);
          })
          .finally(() => dataLoaded());

        queryClient.prefetchQuery("featureFlags", fetchFeatureFlags).finally(() => dataLoaded());

        // Loading all company datas
        for (const company of LocalData.currentUser.companies) {
          totalCount += 2;
          company.load().finally(() => dataLoaded());
          company.members.loadDocument(LocalData.currentUser.id, () => dataLoaded());
        }

        for (const group of LocalData.currentUser.contractorGroups) {
          totalCount += 2;
          group.load().finally(() => dataLoaded());
          group.members.loadDocument(LocalData.currentUser.id, () => dataLoaded());
        }

        for (const property of LocalData.currentUser.properties) {
          totalCount += 2;
          property.load().finally(() => {
            // This is a somewhat hacky/temporary solution because we always need the property data in the local data
            // And therefore we can use the LocalData raw data to update the query data
            const parsedProperty = TRawProperty.safeParse({ ...property.rawData, id: property.id });
            if (parsedProperty.success) {
              queryClient.setQueryData(usePropertyKey(property.id), parsedProperty.data);
            }
            dataLoaded();
          });
          property.members.loadDocument(LocalData.currentUser.id, (propertyMember) => {
            // This is a somewhat hacky/temporary solution because we always need the propertyMember data in the local data
            // And therefore we can use the LocalData raw data to update the query data
            if (propertyMember?.rawData) {
              const parsedPropertyMember = TRawPropertyMember.safeParse({ ...propertyMember.rawData, id: propertyMember.id });
              if (parsedPropertyMember.success) {
                queryClient.setQueryData(usePropertyMemberKey(property.id, LocalData.currentUser.id), parsedPropertyMember.data);
              }
            }
            dataLoaded();
          });
        }

        if (totalCount === 0) dataLoaded();
      } else {
        if (user && user.isExternal) {
          let loaded = 0;
          LocalData.properties.documents.forEach((property) => {
            property.loadWithCompletionHandler(() => {
              loaded++;
              if (loaded >= LocalData.properties.documents.length) completion(false);
            });
          });
          if (LocalData.properties.documents.length === 0) completion(false);
        } else {
          completion(false);
        }
        return;
      }
    }
  }
}

function getUser(completion: (user: UserData | undefined) => void) {
  const auth = getAuth();
  const currentUser = auth.currentUser;

  if (currentUser) {
    setup(currentUser);
  } else {
    onAuthStateChanged(auth, (user) => {
      if (user && user.email) {
        setup(user);
      } else {
        resetPropelyApi();
        completion(undefined);
      }
    });
  }

  function setup(user: User) {
    if (user.isAnonymous) {
      completion(undefined);
    } else {
      const userId = user.uid;
      LocalData.currentUser = LocalData.users.addData(userId);
      LocalData.currentUser.loaded = false;
      LocalData.currentUser.setupRealtimeListener(() => {
        LocalData.currentUser.loadedListeners = [];
        LocalData.currentUser.realtimeListeners = [];
        completion(LocalData.currentUser);
      });
    }
  }
}
